import { LegalPageLayout, LegalSection, LegalList, MailLink } from './LegalPageLayout'

/**
 * Mirrors docs/legal/terms-and-conditions-draft.md. Keep the two in sync — the markdown file is
 * the source of record for legal review; this page is how it actually reaches users.
 */
export default function TermsPage() {
  return (
    <LegalPageLayout title="Terms &amp; Conditions" effectiveDate="2 September 2026">
      <p>
        These Terms &amp; Conditions (&ldquo;Terms&rdquo;) are a contract between you and
        LogMySpends (&ldquo;we,&rdquo; &ldquo;us,&rdquo; &ldquo;our&rdquo;), governing your use of
        the LogMySpends web application (the &ldquo;Service&rdquo;). By creating an account, you
        agree to these Terms.
      </p>

      <LegalSection number={1} title="Eligibility">
        <p>
          You must be at least 18 years old and able to enter a binding contract to create a
          LogMySpends account. The Service is available to users worldwide, but every plan is billed
          in Indian Rupees (INR); any currency conversion is handled by your bank or card issuer.
        </p>
      </LegalSection>

      <LegalSection number={2} title="What LogMySpends is — and isn't">
        <p>
          LogMySpends is a <strong>personal record-keeping tool</strong>. You manually enter your
          own transactions, accounts, budgets, goals, and recurring bills; the app does not connect
          to your bank, does not move money on your behalf (other than the subscription payment
          itself), and does not give financial, investment, or tax advice. Any totals, projections,
          or insights the app shows are calculated from the data you entered — we don&rsquo;t verify
          its accuracy, and you&rsquo;re responsible for checking your own figures before relying on
          them for any real decision.
        </p>
      </LegalSection>

      <LegalSection number={3} title="Accounts and households">
        <p>
          You&rsquo;re responsible for keeping your login credentials secure and for all activity
          under your account. If you create or own a household and invite other members — including
          a spouse, other adult family members, or{' '}
          <strong>children/other non-earning members</strong> — you as the household Owner are
          responsible for:
        </p>
        <LegalList
          items={[
            "having the authority to add that person's name and data to the Service",
            'for a minor: obtaining any consent required under applicable law before adding them as a household member',
            'the accuracy of data entered on behalf of any household member',
          ]}
        />
      </LegalSection>

      <LegalSection number={4} title="Subscription plans and billing">
        <LegalList
          items={[
            <>
              <strong>Free trial:</strong> every new household gets a 30-day free trial with no card
              required. No charge occurs unless you actively subscribe.
            </>,
            <>
              <strong>Plans:</strong> Monthly and Annual, at the prices shown in the app at the time
              of purchase, in INR — both <strong>recurring subscriptions</strong> billed
              automatically at the end of each period via Razorpay, until you cancel. There is no
              one-time or lifetime plan.
            </>,
            <>
              <strong>Auto-renewal:</strong> by subscribing to Monthly or Annual, you authorise us
              to charge your chosen payment method automatically at each renewal, at the
              then-current price for your plan, unless you cancel before the renewal date.
            </>,
            <>
              <strong>Price changes:</strong> we&rsquo;ll give at least 30 days&rsquo; notice by
              email before a price increase takes effect for existing subscribers; it will apply
              from your next renewal, not retroactively.
            </>,
          ]}
        />
      </LegalSection>

      <LegalSection number={5} title="Cancellation and refunds">
        <p>
          You can cancel a Monthly or Annual subscription at any time from Settings → Billing.
          Cancelling stops future renewals and you keep access until the end of the period you
          already paid for. Because every household gets a 30-day free trial to evaluate the Service
          before paying anything, subscription payments are non-refundable once a billing period has
          started — cancelling simply means you are not charged again. This does not affect the
          separate pro-rata refund we commit to if we shut the Service down (see §8).
        </p>
      </LegalSection>

      <LegalSection number={6} title="Acceptable use">
        <p>
          You agree not to: use the Service for any unlawful purpose; attempt to access another
          household&rsquo;s data without authorisation; interfere with or disrupt the
          Service&rsquo;s operation; or reverse-engineer or scrape the Service. We may suspend or
          terminate accounts that violate this section.
        </p>
      </LegalSection>

      <LegalSection number={7} title="Termination">
        <p>
          You may stop using the Service and delete your account at any time. We may suspend or
          terminate your account for a material breach of these Terms, non-payment after a
          reasonable grace period, or fraudulent activity, with notice where practical.
        </p>
      </LegalSection>

      <LegalSection number={8} title="If LogMySpends shuts down">
        <p>
          We intend to run LogMySpends for the long term, and this section exists precisely because
          &ldquo;intend to&rdquo; isn&rsquo;t a guarantee. If we ever decide to permanently
          discontinue the Service, or become unable to continue operating it:
        </p>
        <ol className="list-decimal space-y-2 pl-5">
          <li>
            <strong>Notice.</strong> We will give at least <strong>30 days&rsquo;</strong> advance
            notice by email and an in-app notice to every active user before the Service actually
            shuts down.
          </li>
          <li>
            <strong>Billing stops immediately upon notice.</strong> No further renewal charges will
            be made once shutdown is announced, regardless of your billing cycle date.
          </li>
          <li>
            <strong>Pro-rata refund.</strong> Any household with an active Monthly or Annual
            subscription at the time of the shutdown notice will receive a refund for the unused
            portion of their current paid period, calculated on a straight-line daily basis (e.g.
            cancelling 10 days into a 30-day paid period refunds 20/30 of that period&rsquo;s
            amount).
          </li>
          <li>
            <strong>Data export.</strong> Before shutdown, you will be able to export your
            household&rsquo;s transactions, accounts, budgets, and goals in a machine-readable
            format (CSV or JSON) from the app.
          </li>
          <li>
            <strong>Data deletion after shutdown.</strong> We will delete personal data we hold
            within 90 days of the Service actually shutting down, except where we&rsquo;re legally
            required to retain billing records longer.
          </li>
          <li>
            <strong>No claim beyond the above.</strong> Refunds under this section are calculated
            and paid on the formula above; we don&rsquo;t undertake to compensate for any other loss
            (e.g. time spent using the Service, inconvenience of migrating to another tool).
          </li>
        </ol>
      </LegalSection>

      <LegalSection number={9} title="Disclaimer of warranties">
        <p>
          The Service is provided &ldquo;as is.&rdquo; We don&rsquo;t guarantee it will be
          uninterrupted, error-free, or available at all times. We&rsquo;re not responsible for
          decisions you make based on data or calculations shown in the app — see §2.
        </p>
      </LegalSection>

      <LegalSection number={10} title="Limitation of liability">
        <p>
          To the maximum extent permitted by Indian law, our total liability to you for any claim
          arising from your use of the Service is limited to the amount you paid us in the 12 months
          before the claim arose. We are not liable for indirect, incidental, or consequential
          damages.
        </p>
      </LegalSection>

      <LegalSection number={11} title="Governing law and disputes">
        <p>
          These Terms are governed by the laws of India. Any dispute will first be addressed through
          our Grievance Officer (see the Privacy Policy); if unresolved, the courts of Hyderabad,
          Telangana will have exclusive jurisdiction.
        </p>
      </LegalSection>

      <LegalSection number={12} title="Changes to these Terms">
        <p>
          We may update these Terms from time to time. Material changes will be notified by email or
          in-app notice at least 15 days before they take effect. Continued use after that means you
          accept the updated Terms.
        </p>
      </LegalSection>

      <LegalSection number={13} title="Contact">
        <p>
          Questions about these Terms: <MailLink address="logmyspends@gmail.com" />
        </p>
      </LegalSection>
    </LegalPageLayout>
  )
}
